// Small, dependency-free formatting helpers shared across pages. Everything is
// pure so it can be unit-tested and reused inside charts labels.

/** Format a percentage (already 0..100) with a fixed number of decimals. */
export function pct(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '–';
  return `${value.toFixed(decimals)}%`;
}

/** Format a millisecond value with sensible precision. */
export function ms(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return `${value.toFixed(decimals)} ms`;
}

/** Format a plain count with thousands separators. */
export function count(value: number): string {
  if (!Number.isFinite(value)) return '–';
  return Math.round(value).toLocaleString('en-US');
}

/** Human-readable byte size (SI-ish, base 1000 to match link-rate intuition). */
export function bytes(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '–';
  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  let v = value;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  const decimals = v < 10 && i > 0 ? 1 : 0;
  return `${v.toFixed(decimals)} ${units[i]}`;
}

/** Format a duration given in milliseconds as e.g. "10.0 s" or "2 min 30 s". */
export function duration(msValue: number): string {
  if (!Number.isFinite(msValue) || msValue < 0) return '–';
  const totalSec = msValue / 1000;
  if (totalSec < 60) return `${totalSec.toFixed(totalSec < 10 ? 1 : 0)} s`;
  const min = Math.floor(totalSec / 60);
  const sec = Math.round(totalSec - min * 60);
  return sec === 0 ? `${min} min` : `${min} min ${sec} s`;
}

/**
 * Estimate the on-the-wire size (bytes) of a single probe of the given payload
 * size. The probe frame is 32-byte header + padding to the requested size; on
 * the wire it is wrapped by SCTP/DTLS/UDP/IP. We use a fixed overhead estimate
 * so the data-usage figure is a realistic upper bound rather than payload-only.
 */
export const WIRE_OVERHEAD_BYTES = 90;

/** Wire size for one probe message of the given payload size. */
export function wireSize(payloadSize: number): number {
  return Math.max(32, payloadSize) + WIRE_OVERHEAD_BYTES;
}

/**
 * Data-usage estimate for a run: packets are sent and echoed (both directions),
 * so total bytes = n * wireSize * 2, where n is packets across the duration.
 * Sizes cycle, so we use the mean payload size.
 */
export function estimateRunBytes(
  sizes: number[],
  pps: number,
  durationMs: number,
): number {
  if (sizes.length === 0 || pps <= 0 || durationMs <= 0) return 0;
  const n = Math.round((pps * durationMs) / 1000);
  const meanSize = sizes.reduce((a, b) => a + b, 0) / sizes.length;
  return n * wireSize(meanSize) * 2;
}

/** Format a wall-clock timestamp (ms since epoch) as a short local string. */
export function timestamp(msSinceEpoch: number): string {
  if (!Number.isFinite(msSinceEpoch)) return '–';
  const d = new Date(msSinceEpoch);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Clamp a number into [min, max]. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Parse a comma/space separated list of positive integers, de-duplicated. */
export function parseIntList(text: string): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const token of text.split(/[\s,]+/)) {
    if (!token) continue;
    const n = Number.parseInt(token, 10);
    if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

/** Render a number list back to a comma-separated editable string. */
export function formatIntList(values: number[]): string {
  return values.join(', ');
}
